import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Tasks = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetchTasks();
    }, []);

    const fetchTasks = async () => {
        try {
            const response = await api.get('/tasks');
            setTasks(response.data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            toast.error('Failed to fetch tasks');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (slug) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                await api.delete(`/tasks/by-slug/${slug}`);
                toast.success('Task deleted successfully');
                fetchTasks();
            } catch (error) {
                console.error('Error deleting task:', error);
                toast.error(error.response?.data?.error || 'Failed to delete task');
            }
        }
    };

    const handleEdit = (task) => {
        navigate(`/task/edit/${task.slug}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3366]"></div>
            </div>
        );
    }

    const filteredTasks = tasks.filter(task => filter === 'all' || task.difficulty === filter);

    const getStatsData = () => {
        const beginnerCount = tasks.filter(task => task.difficulty === 'beginner').length;
        const intermediateCount = tasks.filter(task => task.difficulty === 'intermediate').length;
        const advancedCount = tasks.filter(task => task.difficulty === 'advanced').length;
        
        return [
            { title: 'Total Tasks', value: tasks.length, color: 'from-[#FF3366] to-[#FF6B4D]' },
            { title: 'Beginner', value: beginnerCount, color: 'from-green-500 to-emerald-500' },
            { title: 'Intermediate', value: intermediateCount, color: 'from-yellow-500 to-orange-500' },
            { title: 'Advanced', value: advancedCount, color: 'from-red-500 to-pink-500' },
        ];
    };

    return (
        <div className="relative z-10 p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D]">
                        Task Management
                    </h2>
                    <Link
                        to="/task/new"
                        className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium shadow-lg transform hover:translate-y-[-2px]"
                    >
                        Create New Task
                    </Link>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {getStatsData().map((stat, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl border border-[#FF3366]/10 p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-lg bg-black/20">
                                    <div className={`text-[#FF3366]`}>
                                        {stat.title === 'Total Tasks' && (
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {stat.title === 'Beginner' && (
                                            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {stat.title === 'Intermediate' && (
                                            <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {stat.title === 'Advanced' && (
                                            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-300">{stat.title}</h3>
                            </div>
                            <div className="flex items-baseline">
                                <p className="text-3xl font-bold text-white">
                                    {stat.value}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="mb-6 flex gap-4">
                {['All', 'Beginner', 'Intermediate', 'Advanced'].map((level) => (
                    <button
                        key={level}
                        onClick={() => setFilter(level.toLowerCase())}
                        className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all duration-200 ${
                            filter === level.toLowerCase()
                                ? 'bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white shadow-lg'
                                : 'text-gray-400 hover:text-[#FF3366] hover:bg-[#151515] border border-transparent hover:border-[#FF3366]/10'
                        }`}
                    >
                        {level}
                    </button>
                ))}
            </div>

            {/* Tasks Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTasks.map((task) => (
                    <div key={task.id} className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl overflow-hidden shadow-lg border border-[#FF3366]/10 hover:border-[#FF3366]/30 transition-all duration-300 hover:shadow-xl transform hover:translate-y-[-2px]">
                        {/* Task Image */}
                        {task.image_url && (
                            <div className="relative w-full h-48">
                                <img
                                    src={task.image_url?.startsWith('http') ? task.image_url : `${process.env.REACT_APP_API_URL}${task.image_url}`}
                                    alt={task.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        console.error(`Failed to load image: ${task.image_url}`);
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <div className="absolute top-4 right-4">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                                        task.difficulty === 'beginner' 
                                            ? 'bg-green-500/20 text-green-300 border border-green-400/30'
                                            : task.difficulty === 'intermediate'
                                            ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/30'
                                            : 'bg-red-500/20 text-red-300 border border-red-400/30'
                                    }`}>
                                        {task.difficulty}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Task Content */}
                        <div className="p-6">
                            {/* Title and Difficulty (if no image) */}
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-semibold text-white">
                                    {task.title}
                                </h3>
                                {!task.image_url && (
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                        task.difficulty === 'beginner' 
                                            ? 'bg-green-500/10 text-green-400'
                                            : task.difficulty === 'intermediate'
                                            ? 'bg-yellow-500/10 text-yellow-400'
                                            : 'bg-red-500/10 text-red-400'
                                    }`}>
                                        {task.difficulty}
                                    </span>
                                )}
                            </div>

                            {/* Description */}
                            <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                                {task.description}
                            </p>

                            {/* Tags */}
                            {task.tags && task.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {task.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="px-2 py-1 text-xs rounded-full bg-[#0A0A0A] text-[#FF3366] border border-[#FF3366]/20"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 mt-4">
                                <button
                                    onClick={() => handleEdit(task)}
                                    className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0A0A0A] text-[#FF3366] hover:bg-[#FF3366]/10 border border-[#FF3366]/20 transition-all duration-200"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(task.slug)}
                                    className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0A0A0A] text-white hover:bg-red-500/10 hover:text-red-400 border border-transparent hover:border-red-500/20 transition-all duration-200"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredTasks.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">📝</div>
                    <h3 className="text-xl font-semibold text-white mb-2">No tasks found</h3>
                    <p className="text-gray-400 mb-6">
                        {filter === 'all' ? 'Create your first task to get started.' : `No ${filter} tasks found. Try changing the filter.`}
                    </p>
                    <Link
                        to="/task/new"
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium"
                    >
                        Create New Task
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Tasks; 